import { buyerPurchaseStatusLabels } from '@/features/account/purchases';
import { buyerReservationStatusLabels } from '@/features/account/reservations';
import {
  customerStatusLabels,
  deviceStateLabels,
  emailDeliveryLabels,
  paymentStatusLabels,
  saleStatusLabels,
} from '@/features/operations/labels';
import { quotationStatusLabels } from '@/features/operations/quotationStatus';

/**
 * Which color a state is drawn in (#285). Every state belongs to one family, and a family has
 * one color everywhere, so a list can be read at a glance without reading each label:
 *
 * - `pending`: waits for somebody at PhoneTec to act.
 * - `inProgress`: under way, on its own or on the customer's side.
 * - `success`: closed as intended.
 * - `closed`: over without the intended result, by somebody's decision or by time. Not a fault.
 * - `error`: something failed and somebody has to look at it. Also carries an icon, so it is
 *   told apart without relying on color.
 *
 * The texts are not here: they stay in `labels.ts` and the files next to it.
 */
export type StatusFamily = 'pending' | 'inProgress' | 'success' | 'closed' | 'error';

export type StatusDomain =
  | 'quotation'
  | 'device'
  | 'customerAccount'
  | 'emailDelivery'
  | 'reservation'
  | 'payment'
  | 'purchase'
  | 'sale';

/// The family of every state each domain can be in. A state missing here is drawn as `closed`,
/// and the catalog test fails, which is the reminder to classify it.
export const statusFamilies: Record<StatusDomain, Record<string, StatusFamily>> = {
  quotation: {
    QUOTED: 'pending',
    APPROVED: 'success',
    // Rejecting is a decision at the reception, not a failure.
    REJECTED: 'closed',
  },
  device: {
    PENDING_PUBLICATION: 'pending',
    PUBLISHED: 'inProgress',
    RESERVED: 'inProgress',
    SOLD: 'success',
  },
  customerAccount: {
    PENDING: 'pending',
    UNVERIFIED: 'inProgress',
    VERIFIED: 'success',
  },
  emailDelivery: {
    QUEUED: 'inProgress',
    SENT: 'success',
    FAILED: 'error',
    // Nothing to send to, as configured on purpose (the reservations mailbox is optional).
    NOT_CONFIGURED: 'closed',
  },
  reservation: {
    ACTIVE: 'inProgress',
    COMPLETED: 'success',
    EXPIRED: 'closed',
    CANCELLED: 'closed',
  },
  payment: {
    PENDING: 'inProgress',
    APPROVED: 'success',
    // A card Mercado Pago turned down is an ordinary outcome nobody at PhoneTec has to act on.
    // Only a provider that did not answer is a fault (BR-043 keeps both apart).
    REJECTED: 'closed',
    ERROR: 'error',
    CANCELLED: 'closed',
    REFUNDED: 'closed',
  },
  /// An online purchase as the buyer follows it (VEN-7).
  purchase: {
    PROCESSING: 'inProgress',
    CONFIRMED: 'success',
    // Same as the payment it comes from. While the device is still held the buyer can retry,
    // which the page says next to the chip.
    REJECTED: 'closed',
    // Waits for PhoneTec to give the money back.
    REFUND_PENDING: 'pending',
    REFUNDED: 'closed',
    NOT_COMPLETED: 'closed',
  },
  sale: {
    REVERTED: 'closed',
  },
};

/// Where each domain's texts live, so the chip shows the same words as the rest of the panel.
export const statusLabels: Record<StatusDomain, Record<string, string>> = {
  quotation: quotationStatusLabels,
  device: deviceStateLabels,
  customerAccount: customerStatusLabels,
  emailDelivery: emailDeliveryLabels,
  reservation: buyerReservationStatusLabels,
  payment: paymentStatusLabels,
  purchase: buyerPurchaseStatusLabels,
  sale: saleStatusLabels,
};

export function describeStatus(domain: StatusDomain, status: string) {
  return {
    label: statusLabels[domain][status] ?? status,
    family: statusFamilies[domain][status] ?? ('closed' as StatusFamily),
  };
}

export type StatusColors = { text: string; background: string; border: string };

/**
 * The colors of each family, for a light and a dark background. The light ones come from
 * `docs/PhoneTec-design-system/colors_and_type.css`; where a design system color did not reach
 * 4.5:1 as text on its own tint (`--pt-warn`, `--pt-danger`), a darker shade of the same hue
 * is used. The design system has no dark palette, so the dark ones are derived from the same
 * hues. Every pair is checked by the catalog test.
 */
export const statusFamilyColors: Record<'light' | 'dark', Record<StatusFamily, StatusColors>> = {
  light: {
    pending: { text: '#B45309', background: '#FFFBEB', border: '#D97706' },
    inProgress: { text: '#1F69A8', background: '#EAF2FA', border: '#2E7FC4' },
    success: { text: '#048258', background: '#ECFDF5', border: '#0E9F6E' },
    closed: { text: '#4A5159', background: '#F4F6F8', border: '#9BA3AE' },
    error: { text: '#A8161D', background: '#FEF1F2', border: '#DC2626' },
  },
  dark: {
    pending: { text: '#FCD34D', background: '#3B2A0A', border: '#D97706' },
    inProgress: { text: '#93C5FD', background: '#0F2A44', border: '#2E7FC4' },
    success: { text: '#6EE7B7', background: '#06372A', border: '#0E9F6E' },
    closed: { text: '#D5D9DF', background: '#2A2F36', border: '#6B7480' },
    error: { text: '#FCA5A5', background: '#4A1013', border: '#DC2626' },
  },
};

/// How the documentation and the accessible description name each family.
export const statusFamilyNames: Record<StatusFamily, string> = {
  pending: 'Pendiente',
  inProgress: 'En curso',
  success: 'Cerrado con éxito',
  closed: 'Cerrado sin éxito',
  error: 'Error',
};
